import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Inject,
    PLATFORM_ID,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {SearchPanelComponent} from '../search-panel/search-panel.component';
import {CommonConstants, ImagePathConstants, RoutesConstants, ImageSizeConstants} from '@coreconstant';
import {ActivatedRoute, NavigationEnd, Router, RouterModule} from '@angular/router';
import {debounceTime, distinctUntilChanged, lastValueFrom, Subject} from 'rxjs';
import {ResponseHandlerService} from 'src/app/core/services/common-service/response-handler.service';
import {CommonBehaviorChangeService} from 'src/app/core/services/common-service/common-behavior-change.service';
import {CommonModule, isPlatformBrowser} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {SearchService} from '../../core/services/api-service/search.service';
import {RequestParamModel} from '../../core/models/request-param-model';
import {SettingService} from '../../core/services/api-service/setting.service';

declare const bootstrap: any;

@Component({
    selector: 'app-header',
    imports: [SearchPanelComponent, RouterModule, CommonModule, FormsModule],
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class HeaderComponent {
    isSticky: boolean = false;
    requestParamModel!: RequestParamModel;
    imageSizeConstants = ImageSizeConstants
    routesConstants = RoutesConstants
    commonConstants = CommonConstants
    imagePathConstants = ImagePathConstants
    firstSegment: string;
    settingData: any;
    headerURL: any;
    headerTitle: any;
    isHideSearchSection: boolean = true
    searchKeyword: string = ''
    venues: [];
    vendors: [];
    blogs: [];
    searchData: any;
    searchInput: any;
    currentRoute: string = '';
    @ViewChild('searchIcon') searchIcon!: ElementRef;
    @ViewChild('searchTextbox') searchTextbox!: ElementRef;
    @ViewChild('searchTextboxDesktop') searchTextboxDesktop!: ElementRef;
    @ViewChild('searchContainer') searchContainer!: ElementRef;
    isShowSearchIcon: boolean = false
    isMobileView: boolean = false
    isShowCancelIcon: boolean = false
    private searchSubject = new Subject<string>();
    isLoader: boolean = false
    isSearchOpen = false;

    /**
     * Handles the window scroll event to determine the currently active section
     * in the viewport based on predefined section IDs.
     */
    @HostListener('window:scroll', [])
    onWindowScroll(): void {
        this.isSticky = window.scrollY > 10;
    }

    /**
     * HostListener method that reacts to window resize events.
     * Checks if the platform is a browser and updates the `isMobileView` property based on the window's
     * */
    @HostListener('window:resize', [])
    onWindowResize() {
        if (isPlatformBrowser(this.platformId)) {
            this.isMobileView = window.innerWidth < 1100;
        }
    }

    /**
     * Handles click events on the document and determines whether to hide the search section based on click location outside specific elements.
     * @param {Event} event - The click event captured on the document.
     */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event) {
        if (
            this.searchIcon &&
            !this.searchIcon.nativeElement.contains(event.target) &&
            this.searchTextbox &&
            !this.searchTextbox.nativeElement.contains(event.target) &&
            this.searchContainer &&
            !this.searchContainer.nativeElement.contains(event.target)
        ) {
            this.isHideSearchSection = true
            this.isSearchOpen = false
        }
    }

    constructor(
        private _router: Router,
        private _settingService: SettingService,
        private _searchService: SearchService,
        private _responseHandlerService: ResponseHandlerService,
        private _commonBehaviorChangeService: CommonBehaviorChangeService,
        @Inject(PLATFORM_ID) private platformId: Object,
        private _changeDetectorRef: ChangeDetectorRef,
        private _activatedRoute: ActivatedRoute
    ) {
        this.onWindowResize()

        this._activatedRoute.queryParams.subscribe(params => {
            this.searchInput = params['search']
            this.searchKeyword = this.searchInput ?? ''
        });
    }

    /**
     * Initializes the component and its dependent processes.
     */
    ngOnInit(): void {
        this.requestParamModel = new RequestParamModel();
        this.searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged()
        ).subscribe((searchText) => {
            if (searchText.length >= 3) {
                this.getSearchResult();
            }
        });

        this.currentRoute = this._router.url.split('?')[0];
        this.getSettings();
        this.handleActiveMenu();
        this._router.events.subscribe((event) => {
            this.handleActiveMenu();

            if (event instanceof NavigationEnd) {
                this.currentRoute = this._router.url.split('?')[0];
                if (this.currentRoute != '/' + this.routesConstants.SEARCH) {
                    this.searchInput = ''
                }
            }

        });
    }

    /**
     * Handles the search input logic, updating the state based on the user's input or actions.
     * @param {KeyboardEvent} event - The keyboard event triggered by user interaction (e.g., key presses such as Backspace, Delete, or other input).
     */
    searchValue(event) {
        if (event.key === 'Backspace' || event.key === 'Delete') {
            this.searchValueChange(event);
        }
        this.isShowCancelIcon = false
        const value = event.target.value;
        if (value.length >= 3) {
            this.isShowCancelIcon = true
            this.searchValueChange(event)
        }
    }

    /**
     * Handles the logic for activating and toggling the menu based on the current URL.
     * Updates the first segment of the URL and performs menu toggle action.
     */
    handleActiveMenu() {
        const currentUrl = this._router.url;
        this.firstSegment = this.getFirstSegment(currentUrl);
        this.toggleMenu();
    }

    /**
     * Extracts the first segment of a given URL path.
     * @param {string} url - The URL from which the first segment will be extracted.
     */
    getFirstSegment(url: string): string {
        const segments = url.split('/');
        return segments.length > 1 ? `/${segments[1]}` : '';
    }


    /**
     * Retrieves and processes system settings.
     */
    getSettings() {
        let getSettings = this._settingService.getSettings()
        lastValueFrom(getSettings).then((res: any) => {
            if (res["status"]) {
                this.settingData = res["data"];
                const headerData = this.settingData.find((item: any) => item.section == 'header');
                this.headerURL = headerData?.url;
                this.headerTitle = headerData?.title;
                this._commonBehaviorChangeService.setSystemSettings(this.settingData);
            } else {
                this._responseHandlerService.showErrorMessageGeneral(res["message"])
            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error)
        });
    }

    /**
     * Toggles the visibility of the navigation menu.
     * @param {any} [menu] Optional parameter that can be used for additional logic related to the toggle functionality.
     */
    toggleMenu(menu?: any) {
        if (isPlatformBrowser(this.platformId)) {
            const navbarMenu = document.getElementById('navbarMenu');
            if (navbarMenu) {
                const bsCollapse = new bootstrap.Collapse(navbarMenu, {
                    toggle: false
                });
                bsCollapse.hide()
            }
        }
    }

    /**
     * Handles the click event for the search box.
     */
    searchBoxClick() {
        if (this.currentRoute != '/' + this.routesConstants.SEARCH)
            this.isHideSearchSection = false
    }

    /**
     * Handles changes to the search input value, performs search operations, and navigates to the search page if required.
     * @param {Event} event - The event object triggered on input or key press actions in the search field.
     */
    searchValueChange(event: Event) {
        this.searchKeyword = this.searchInput;

        if (event['key'] == 'Enter') {
            this.closeSearchSection(true)

            let queryParams = {search: this.searchKeyword}
            if (this.currentRoute != '/' + this.routesConstants.SEARCH) {
                queryParams['type'] = this.commonConstants.MODULE_ALL
            }
            this._router.navigate([this.routesConstants.SEARCH], {
                relativeTo: this._activatedRoute,
                queryParams: queryParams,
                queryParamsHandling: 'merge'
            });

        } else {
            if (this.currentRoute != '/' + this.routesConstants.SEARCH) {
                if (this.searchKeyword.length >= 3) {
                    this.searchSubject.next(this.searchKeyword);
                }
            }
        }
    }

    /**
     * Executes a search operation based on the current search keyword and updates the search results.
     */
    getSearchResult() {
        if (this.searchKeyword.length > 0) {
            this.requestParamModel.removeFilter('search')
            this.requestParamModel.setFilter('search', this.searchKeyword)
            this.isLoader = true
            delete this.requestParamModel.pageSize;
            let getData = this._searchService.search(this.requestParamModel)
            lastValueFrom(getData).then((res: any) => {
                if (res["data"]) {
                    this.searchData = res["data"];
                    this.isLoader = false
                } else {
                    this._responseHandlerService.showErrorMessageGeneral(res["message"] ?? this.commonConstants.SOMETHING_WENT_WRONG)
                }
            }, (error: any) => {
                this.isLoader = false
                this._responseHandlerService.handleAPIErrorResponse(error)
            });
        }

    }

    /**
     * Closes the search section by updating relevant state variables to hide it.
     * @param {any} event - The event object that triggered the method.
     */
    closeSearchSection(event: any) {
        this.isHideSearchSection = true
        this.isSearchOpen = false
    }

    /**
     * Clears the search input fields and hides the search section.
     * @param {any} event - An event object that triggers this method.
     */
    clearSearchSection(event: any) {
        this.searchKeyword = ''
        this.searchInput = ''
        this.isHideSearchSection = true
    }

    /**
     * Toggles the visibility state of the search icon for the mobile interface.
     * Also ensures the search section is closed when toggling.
     */
    searchToggleMobile() {
        this.isShowSearchIcon = !this.isShowSearchIcon
        this.closeSearchSection(true)
    }

    /**
     * Clears the current search input and resets relevant search-related properties.
     * */
    clearSearchOnClose() {
        this.searchKeyword = ''
        this.isShowCancelIcon = false
        this.searchInput = ''
        this.searchData = []
        this.isHideSearchSection = true
        if (this.currentRoute == '/' + this.routesConstants.SEARCH) {
            this._router.navigate([this.routesConstants.SEARCH], {
                relativeTo: this._activatedRoute,
                queryParams: {type: this.commonConstants.MODULE_ALL, search: ''},
                queryParamsHandling: 'merge'
            });
        }
    }

    /**
     * Lifecycle hook that is called when the component is destroyed.
     */
    ngOnDestroy() {
        this.searchInput = ''
        this.searchSubject.unsubscribe();
    }

    /**
     * Toggles the state of the search icon and manages associated UI updates.
     * Updates the state of `isSearchOpen`, triggers change detection,
     * focuses on the search textbox, and invokes the search box click handler.
     *
     * @return {void} Does not return a value.
     */
    manageSearchIcon() {
        this.isSearchOpen = !this.isSearchOpen;
        this._changeDetectorRef.detectChanges()
        setTimeout(() => {
            this.searchTextbox?.nativeElement.focus();
            this.searchBoxClick()
        }, 100);
    }

    /**
     * Prevents the default behavior and stops propagation of the given event, effectively disabling any double-click action or event bubbling.
     * @param {Event} event - The event object associated with the double-click action to be stopped.
     */
    stopDoubleClick(event: Event) {
        event.preventDefault();
        event.stopPropagation();
    }
}
